import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { LexemePublishStatus } from '@sayyesschool/common/entities/lexeme';
import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import { API_URL } from '@sayyesschool/frontend/constants';
import { useDictionaryApi } from '@sayyesschool/frontend/features/dictionary';
import {
    Lexeme,
    LexemesList,
    LexemesSearch
} from '@sayyesschool/frontend/features/lexemes';
import { Dialog, IconButton } from '@sayyesschool/frontend/ui-components';

import LexemeForm from '../../dictionary/lexeme-form';

import styles from './VocabularyItem.module.scss';

const SEARCH_URL = `${API_URL}/dictionary/search`;

function VocabularyItemForm({ lexemeIds = [] }, ref) {
    const dictionaryApi = useDictionaryApi();

    const lexemeIdsRef = useRef(lexemeIds);

    const [lexemes, setLexemes] = useState(null);
    const [viewingLexeme, setViewingLexeme] = useState();
    const [editingLexeme, setEditingLexeme] = useState();

    useImperativeHandle(ref, () => ({
        get props() {
            return {
                lexemeIds: lexemeIdsRef.current
            };
        }
    }));

    useEffect(() => {
        dictionaryApi.getLexemes(lexemeIdsRef.current)
            .then(lexemes => setLexemes(lexemes));
    }, []);

    const addLexeme = useCallback(lexeme => {
        lexemeIdsRef.current.push(lexeme.id);
        setLexemes(lexemes => lexemes.concat(lexeme));
    }, []);

    const removeLexeme = useCallback(lexeme => {
        lexemeIdsRef.current = lexemeIdsRef.current.filter(id => id !== lexeme.id);
        setLexemes(lexemes => lexemes.filter(l => l.id !== lexeme.id));
    }, []);

    const handleAddLexeme = useCallback(lexeme => {
        return dictionaryApi.createLexeme({
            ...lexeme,
            publishStatus: LexemePublishStatus.Approved
        }).then(response => {
            addLexeme(response);
        });
    }, [dictionaryApi, addLexeme]);

    const handleUpdateLexeme = useCallback(data => {
        return dictionaryApi.updateLexeme(editingLexeme?.id, data)
            .then(() => {
                setLexemes(lexemes => lexemes.map(l => l.id === editingLexeme.id ? data : l));
            })
            .finally(() => setEditingLexeme(null));
    }, [dictionaryApi, editingLexeme?.id]);

    if (!lexemes) return <LoadingIndicator />;

    return (
        <div className={styles.root}>
            <LexemesSearch
                key={lexemes.length}
                className={styles.search}
                url={SEARCH_URL}
                lexemes={lexemes}
                // TODO: в LexemesSearch params почему-то не обрабатывается, надо глянуть почему так
                params={{ publishStatus: LexemePublishStatus.Approved }}
                renderResultItemAction={result =>
                    <IconButton
                        icon="add"
                        title="Добавить слово"
                        variant="soft"
                        onClick={() => addLexeme(result.data)}
                    />
                }
                onAddLexeme={handleAddLexeme}
                onSelectLexeme={addLexeme}
            />

            <LexemesList
                lexemes={lexemes}
                renderLexemeActions={lexeme => [
                    {
                        key: 'edit',
                        icon: 'edit',
                        title: 'Редактировать слово',
                        onClick: () => setEditingLexeme(lexeme)
                    },
                    {
                        key: 'remove',
                        icon: 'remove',
                        title: 'Убрать слово',
                        onClick: () => removeLexeme(lexeme)
                    }
                ]}
                onViewLexeme={setViewingLexeme}
            />

            <Dialog
                open={!!viewingLexeme}
                onClose={() => setViewingLexeme(null)}
            >
                <Lexeme
                    lexeme={viewingLexeme}
                    readOnly
                />
            </Dialog>

            <FormDialog
                title="Редактирование лексемы"
                open={!!editingLexeme}
                onClose={() => setEditingLexeme(null)}
            >
                <LexemeForm
                    id="lexeme-edit-form"
                    lexeme={editingLexeme}
                    onSubmit={handleUpdateLexeme}
                />
            </FormDialog>
        </div>
    );
}

export default forwardRef(VocabularyItemForm);